import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Edit, Trash2, User, Loader2 } from "lucide-react";

export default function AdminBarbers() {
  const { token } = useAuth();
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBarber, setEditingBarber] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    specialty: "",
    bio: "",
    image_url: "",
    phone: ""
  });
  const [saving, setSaving] = useState(false);

  const fetchBarbers = async () => {
    try {
      const response = await axios.get(`${API}/barbers?active_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBarbers(response.data);
    } catch (error) {
      toast.error("Berberler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBarbers();
  }, [token]);

  const openAddDialog = () => {
    setEditingBarber(null);
    setFormData({
      name: "",
      specialty: "",
      bio: "",
      image_url: "",
      phone: ""
    });
    setDialogOpen(true);
  };

  const openEditDialog = (barber) => {
    setEditingBarber(barber);
    setFormData({
      name: barber.name,
      specialty: barber.specialty || "",
      bio: barber.bio || "",
      image_url: barber.image_url || "",
      phone: barber.phone || ""
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Berber adı gerekli");
      return;
    }

    setSaving(true);
    try {
      if (editingBarber) {
        await axios.put(
          `${API}/barbers/${editingBarber.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Berber güncellendi");
      } else {
        await axios.post(
          `${API}/barbers`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Berber eklendi");
      }
      setDialogOpen(false);
      fetchBarbers();
    } catch (error) {
      toast.error("İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu berberi silmek istediğinizden emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/barbers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Berber silindi");
      fetchBarbers();
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    }
  };

  const toggleActive = async (barber) => {
    try {
      await axios.put(
        `${API}/barbers/${barber.id}`,
        { is_active: !barber.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(barber.is_active ? "Berber pasif yapıldı" : "Berber aktif yapıldı");
      fetchBarbers();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const toggleHomepage = async (barber) => {
    try {
      await axios.put(
        `${API}/barbers/${barber.id}`,
        { show_on_homepage: !barber.show_on_homepage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(barber.show_on_homepage ? "Ana sayfadan kaldırıldı" : "Ana sayfaya eklendi");
      fetchBarbers();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  return (
    <AdminLayout title="Berber Yönetimi">
      <div data-testid="admin-barbers">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <p className="text-[#6b7280]">{barbers.length} berber kayıtlı</p>
          <Button onClick={openAddDialog} className="btn-primary" data-testid="add-barber-btn">
            <Plus className="w-4 h-4 mr-2" />
            Berber Ekle
          </Button>
        </div>

        {/* Barbers Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {barbers.map((barber) => (
              <div 
                key={barber.id}
                className={`bg-white border border-[#e5e7eb] rounded-xl overflow-hidden shadow-sm ${!barber.is_active ? "opacity-50" : ""}`}
                data-testid={`barber-card-${barber.id}`}
              >
                <div className="relative">
                  <img 
                    src={barber.image_url || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400"} 
                    alt={barber.name}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-2 right-2 flex gap-1">
                    {!barber.is_active && (
                      <span className="bg-red-600 text-white text-xs px-2 py-1 rounded font-accent">
                        PASİF
                      </span>
                    )}
                    {!barber.show_on_homepage && barber.is_active && (
                      <span className="bg-[#9ca3af] text-white text-xs px-2 py-1 rounded font-accent">
                        GİZLİ
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="p-4">
                  <h3 className="font-display text-lg text-[#1a1a2e] mb-1">{barber.name}</h3>
                  <p className="font-accent text-xs text-[#c9a96e] mb-2">{barber.specialty || "Genel"}</p>
                  <p className="text-[#6b7280] text-sm line-clamp-2 mb-4">{barber.bio || "—"}</p>
                  
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <Button
                        onClick={() => openEditDialog(barber)}
                        variant="ghost"
                        className="flex-1 text-[#6b7280] hover:text-[#1a1a2e]"
                        data-testid={`edit-barber-${barber.id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Düzenle
                      </Button>
                      <Button
                        onClick={() => toggleActive(barber)}
                        variant="ghost"
                        className={`flex-1 ${barber.is_active ? "text-amber-500" : "text-emerald-600"}`}
                      >
                        {barber.is_active ? "Pasif" : "Aktif"}
                      </Button>
                    </div>
                    <Button
                      onClick={() => toggleHomepage(barber)}
                      variant="ghost"
                      className={`w-full ${barber.show_on_homepage ? "text-[#9ca3af]" : "text-[#c9a96e]"}`}
                      data-testid={`toggle-homepage-${barber.id}`}
                    >
                      {barber.show_on_homepage ? "Ana Sayfadan Kaldır" : "Ana Sayfada Göster"}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-white border-[#e5e7eb] text-[#1a1a2e]">
            <DialogHeader>
              <DialogTitle className="font-display">
                {editingBarber ? "Berber Düzenle" : "Yeni Berber Ekle"}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="font-accent text-xs text-[#6b7280] mb-2 block">AD SOYAD *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="input-elite"
                  placeholder="Berber adı"
                  data-testid="barber-name-input"
                />
              </div>
              
              <div>
                <label className="font-accent text-xs text-[#6b7280] mb-2 block">UZMANLIK</label>
                <Input
                  value={formData.specialty}
                  onChange={(e) => setFormData(prev => ({ ...prev, specialty: e.target.value }))}
                  className="input-elite"
                  placeholder="Örn: Modern Kesimler, Sakal Uzmanı"
                />
              </div>
              
              <div>
                <label className="font-accent text-xs text-[#6b7280] mb-2 block">BİYOGRAFİ</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                  className="w-full h-20 input-elite resize-none"
                  placeholder="Kısa biyografi"
                />
              </div>
              
              <div>
                <label className="font-accent text-xs text-[#6b7280] mb-2 block">FOTOĞRAF URL</label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                  className="input-elite"
                  placeholder="https://..."
                />
              </div>
              
              <div>
                <label className="font-accent text-xs text-[#6b7280] mb-2 block">TELEFON</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="input-elite"
                  placeholder="+90 5XX XXX XX XX"
                />
              </div>
              
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-[#6b7280]">
                  İptal
                </Button>
                <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-barber-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
